import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { map, Observable, scan, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Download, Upload } from '../modals/file-upload-new/file-upload-new.interface';
import { CHANNEL_TYPE } from '../enum/constants';
import { createQueryParams } from '../helpers';
import { SortField, SortOrder, Space } from '../modules/crm/file-system/components/file-system/file-system.interface';
import { SPACE_TYPE } from '../modules/crm/file-system/components/file-system/file-system.constants';


@Injectable({
  providedIn: 'root',
})
export class FileSystemService {
  private directoryUpdatedSource = new Subject<void>();

  directoryUpdated$ = this.directoryUpdatedSource.asObservable();

  constructor(private readonly http: HttpClient) {
  }

  updateDirectory() {
    this.directoryUpdatedSource.next();
  }

  public getDirectory(params: GetDirectoryParams): Observable<any> {
    const httpParams = createQueryParams(params);
    return this.http.get<any>(`${environment.baseURL}/file-system/directory`, { params: httpParams })
      .pipe(map(res => res.data));
  }

  public getSpaceInfo(spaceType?: SPACE_TYPE): Observable<Space> {
    const params = createQueryParams({ space_type: spaceType });
    return this.http.get<any>(environment.baseURL + '/file-system/space-info', { params })
      .pipe(map(res => res.data));
  }

  public createDirectory(directoryId?: string) {
    const body: any = {};
    if (directoryId) body.parent_id = directoryId;
    return this.http.post<any>(environment.baseURL + '/file-system/directory', body)
      .pipe(map(res => res.data));
  }

  public updateNameDirectory(id: string, name: string) {
    const body: any = {
      name,
    };
    return this.http.put<any>(environment.baseURL + `/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  public delete(id: string): Observable<any> {
    return this.http.delete<any>(environment.baseURL + `/file-system/${id}`);
  }

  public moveDirectory(id: string, parent_id: string) {
    const body: any = {
      parent_id,
    };
    return this.http.put<any>(environment.baseURL + `/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  updateFSItem(id: string, body: IUpdateFSItem): Observable<any> {
    return this.http.put<any>(`${environment.baseURL}/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  restore(id: string) {
    const body: any = {
      restore: true,
    };
    return this.http.put<any>(environment.baseURL + `/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  togglePinned(id: string) {
    const body: any = {
      pinnded: true,
    };
    return this.http.put<any>(environment.baseURL + `/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  toggleShared(id: string) {
    const body: any = {
      toggle_shared: true,
    };
    return this.http.put<any>(`${environment.baseURL}/file-system/directory/${id}`, body)
      .pipe(map(res => res.data));
  }

  uploadFile(file: any, parent_id?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, encodeURIComponent(file.name));
    if (parent_id) formData.append('parent_id', parent_id);
    const initialState: Upload = { state: false, progress: 0 };

    return this.http.post<any>(environment.baseURL + '/file-system/file/upload', formData, {
      reportProgress: true,
      observe: 'events',
    }).pipe(scan(calculateStateUpload, initialState));
  }

  downloadArchive(itemIds: string[]): Observable<Download> {
    const params = createQueryParams({ ids: itemIds });
    const url = `${environment.baseURL}/file-system/archive/download`;

    const initialState: Download = { state: false, progress: 0, content: null };

    return this.http.get(url, {
      params,
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
    }).pipe(scan(calculateStateDownload, initialState));
  }

  getFile(fileId: string, options?: { thumbnail?: boolean; progress?: boolean }): Observable<Blob | Download> {
    const { thumbnail = false, progress = false } = options || {};

    const url = `${environment.baseURL}/file-system/assets/${fileId}`;

    if (progress) {
      const initialState: Download = { state: false, progress: 0, content: null };

      return this.http.get(url, {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
      }).pipe(scan(calculateStateDownload, initialState));
    }

    return this.http.get(url, {
      params: { thumbnail },
      responseType: 'blob',
    });
  }

  /** `path` is the files-service upload route: `temp` by default, `avatar` stores permanently under `avatars/` */
  uploadTempFile(file: File, channelType = CHANNEL_TYPE.UNKNOWN, path: 'temp' | 'avatar' = 'temp') {
    const formData = new FormData();
    formData.append('file', file);

    if (channelType !== CHANNEL_TYPE.UNKNOWN) formData.append('channel_type', channelType);

    const initialState: Upload = { state: false, progress: 0 };
    return this.http.post<any>(environment.filesURL + `/files/${path}`, formData, {
      reportProgress: true,
      observe: 'events',
    }).pipe(scan(calculateStateUpload, initialState));
  }

  deleteTempFile(key: string) {
    return this.http.delete<any>(environment.baseURL + `/file-system/file/${key}/delete`);
  }
}

const calculateStateDownload = (download: Download, event: HttpEvent<unknown>): Download => {
  if (isHttpProgressEvent(event)) {
    return {
      progress: event.total
        ? Math.round((100 * event.loaded) / event.total)
        : download.progress,
      state: false,
      content: null,
    };
  }
  if (isHttpResponse(event)) {
    const data: any = {
      progress: 100,
      state: true,
      content: event?.body,
    };

    if (event?.body) {
      data.body = event.body;
      data.fileUrl = environment.baseURL + '/assets/' + data.body.id;
    }
    return data;
  }
  return download;
};

const calculateStateUpload = (upload: Upload, event: HttpEvent<unknown>): Upload => {
  if (isHttpProgressEvent(event)) {
    return {
      progress: event.total
        ? Math.round((100 * event.loaded) / event.total)
        : upload.progress,
      state: false,
    };
  }
  if (isHttpResponse(event)) {
    const data: any = {
      progress: 100,
      state: true,
    };

    if (event?.body) {
      data.body = event.body;
      data.fileUrl = environment.baseURL + '/assets/' + data.body.data.id;
    }

    return data;
  }
  return upload;
};


function isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
  return event.type === HttpEventType.Response;
}

function isHttpProgressEvent(
  event: HttpEvent<unknown>,
): event is HttpProgressEvent {
  return (
    event.type === HttpEventType.DownloadProgress ||
    event.type === HttpEventType.UploadProgress
  );
}

export interface GetDirectoryParams {
  parent_id?: string;
  search?: string;
  sort?: SortField;
  order?: SortOrder;
  page: number;
  space_type: SPACE_TYPE;
  only_shared?: boolean;
  file_type?: string;
  created_by?: string;
}

export interface IUpdateFSItem {
  is_pinned?: boolean;
  name?: string;
  restore?: boolean;
  parent_id?: string;
  is_shared?: boolean;
}
